import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { BillEorCodesResponse } from '../../../../models/api/view/claim/financials/bills/bill-eor-codes-response';
import { BillLinesResponse } from '../../../../models/api/view/claim/financials/bills/bill-lines-response';
import { BillDataTransformer } from '../../../../utilities/bill-data-transformer';
import { EorCodeMatcher } from '../../../../utilities/eor-code-matcher';
import { ExportFormat } from '../../../core/buttons/export-button.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-eor-service-detail',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    TableComponent,
    ColumnComponent,
    DetailPanelComponent,
    DetailItemComponent,
  ],
  exportAs: 'eorServiceDetail',
  template: `
    <div class="tw-flex tw-basis-full tw-gap-4 tw-pb-4">
      <app-card
        label="EOR service detail"
        class="tw-grow"
        [expanded]="false"
        [showExpand]="true"
        theme="Light"
      >
        <app-table
          [dataSource]="billLines"
          [scrollable]="false"
          dataKey="lineNum"
          recordLabel="service"
          [showFilterButton]="false"
          [showFilter]="false"
          [showRecordLabel]="true"
          [showPaginator]="false"
          [showExport]="true"
          (export)="onExport($event)"
          [expandable]="true"
        >
          <app-column name="dosBegin" label="Service date" format="d" />
          <app-column name="procMod" label="Procedure modifier(s)" />
          <app-column name="billedQty" label="Units" />
          <app-column name="billedAmount" label="Billed amount" format="c" />
          <app-column name="feeReduction" label="FS/UCR reduction" format="c" />
          <app-column
            name="otherReduction"
            label="Negotiated discount"
            format="c"
          />
          <app-column
            name="ppoReduction"
            label="Network reduction"
            format="c"
          />
          <app-column
            name="recAmount"
            label="Recommended allowance"
            format="c"
          />
          <ng-template #expand let-data>
            <div class="tw-flex">
              <div class="tw-basis-1/3 tw-pr-8">
                <app-detail-panel [dataSource]="data">
                  <app-detail-item
                    label="Procedure description"
                    name="procDescr"
                  />
                </app-detail-panel>
              </div>
              <div class="tw-basis-1/3 tw-pr-8">
                <app-detail-panel [dataSource]="data">
                  <app-detail-item
                    label="Submitted code"
                    name="procCodeBilled"
                  />
                </app-detail-panel>
              </div>
              <div class="tw-basis-1/3 tw-pr-8">
                <app-detail-panel [dataSource]="data">
                  <app-detail-item
                    label="Reimbursed code"
                    name="procCodePaid"
                  />
                </app-detail-panel>
              </div>
            </div>

            <div class="tw-flex tw-gap-8">
              @if (getReductionCodesForLine(data).length > 0) {
                <div class="tw-basis-1/3">
                  <app-card
                    label="Reduction codes"
                    class="tw-grow"
                    [expanded]="true"
                    [showExpand]="false"
                  >
                    <div class="tw-p-2">
                      @for (
                        code of getReductionCodesForLine(data);
                        track code.codeId
                      ) {
                        <app-detail-panel [dataSource]="code">
                          <app-detail-item
                            [label]="code.codeId"
                            name="codeDescription"
                          />
                        </app-detail-panel>
                      }
                    </div>
                  </app-card>
                </div>
              }
              @if (getReasonCodesForLine(data).length > 0) {
                <div class="tw-basis-1/3">
                  <app-card
                    label="Reason codes"
                    class="tw-grow"
                    [expanded]="true"
                    [showExpand]="false"
                  >
                    <div class="tw-p-2">
                      @for (
                        code of getReasonCodesForLine(data);
                        track code.codeId
                      ) {
                        <app-detail-panel [dataSource]="code">
                          <app-detail-item
                            [label]="code.codeId"
                            name="codeDescription"
                          />
                        </app-detail-panel>
                      }
                    </div>
                  </app-card>
                </div>
              }
              @if (getGroupCodesForLine(data).length > 0) {
                <div class="tw-basis-1/3">
                  <app-card
                    label="Group codes"
                    class="tw-grow"
                    [expanded]="true"
                    [showExpand]="false"
                  >
                    <div class="tw-p-2">
                      @for (
                        code of getGroupCodesForLine(data);
                        track code.codeId
                      ) {
                        <app-detail-panel [dataSource]="code">
                          <app-detail-item
                            [label]="code.codeId"
                            name="codeDescription"
                            labelCase="upper"
                          />
                        </app-detail-panel>
                      }
                    </div>
                  </app-card>
                </div>
              }
            </div>
          </ng-template>
        </app-table>
      </app-card>
    </div>
  `,
})
export class EorServiceDetailComponent {
  @Input() billLinesData: BillLinesResponse | null = null;
  @Input() billEorCodes: BillEorCodesResponse | null = null;
  @Output() export = new EventEmitter<ExportFormat>();

  @ViewChildren(CardComponent) cards!: QueryList<CardComponent>;
  toggleCards(expanded: boolean) {
    if (this.cards) {
      this.cards.forEach((card) => {
        card.expanded = expanded;
      });
    }
  }

  onExport(format: ExportFormat) {
    this.export.emit(format);
  }

  get billLines() {
    return BillDataTransformer.getBillLines(this.billLinesData);
  }

  getReductionCodesForLine(lineData: any) {
    return EorCodeMatcher.getReductionCodes(
      lineData,
      this.billEorCodes?.reductionCodes,
    );
  }

  getReasonCodesForLine(lineData: any) {
    // Return all reviewExplanations without matching by code
    return this.billEorCodes?.reviewExplanations || [];
  }

  getGroupCodesForLine(lineData: any) {
    return EorCodeMatcher.getGroupCodes(
      lineData,
      this.billEorCodes?.groupCodes,
    );
  }
}
